import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { IAjax } from 'lib/Ajax';
import TZDate from 'lib/TZDate';
import UOM from 'lib/UOM';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { Entrypoints } from 'core/app/entrypoints';
import { PCCache } from 'data/PCCache';
import { Country } from 'data/models/Country';
import { DomainSettings } from 'data/models/DomainSettings';
import { NullableMeasurement } from 'data/models/Measurement';
import { LloydServiceVessel } from 'data/models/LloydServiceVessel';
import { MarineTrafficServiceVessel } from 'data/models/MarineTrafficServiceVessel';
import { OverrideServiceVessel } from 'data/models/OverrideServiceVessel';
import { PSixServiceVessel } from 'data/models/PSixServiceVessel';
import { Vessel } from 'data/models/Vessel';
import { VesselServiceOverrideResponseData } from 'data/models/VesselServiceOverrideResponse';
import { VesselServiceOverrideCache } from './cache';

export interface VesselViewModel {
    Model : Vessel;
    OverrideServiceVessel : () => OverrideServiceVesselViewModel | null;
    Name : () => string | null;
    VIN : () => string | null;
    IMO : () => string | null;
    MMSID : () => string | null;
    CallSign : () => string | null;
    Beam : () => NullableMeasurement;
    Length : () => NullableMeasurement;
    GrossTonnage : () => NullableMeasurement;
    IsGovernmentVessel : () => boolean | null;
    VesselType : () => string | null;
    IsCruiseVessel : () => boolean | null;
    MaxDraft : () => NullableMeasurement;
    ExpectedPassengerCount : () => number | null;
    Flag : () => Country | null;
}

export interface OverrideServiceVesselViewModel {
    Model : OverrideServiceVessel;
    CallSign : (val? : string) => string | null;
    Name : (val? : string) => string | null;
    IMO : (val? : string) => string | null;
    VesselType : (val? : string) => string | null;
}

export interface VesselServiceOverrideParams {
    id : number;
}

type BaseVesselProperty<T> = (v : Vessel | OverrideServiceVessel | LloydServiceVessel | MarineTrafficServiceVessel | PSixServiceVessel) => T;
type VesselPrecedenceOrder = (((v : Vessel) => DataSignal<LloydServiceVessel | null>) | ((v : Vessel) => DataSignal<MarineTrafficServiceVessel | null>) | ((v : Vessel) => DataSignal<PSixServiceVessel | null>))[];
const defaultVesselPrecedenceOrder : VesselPrecedenceOrder = [v => v.LloydServiceVessel, v => v.MarineTrafficServiceVessel, v => v.PSixServiceVessel];

export function VesselServiceOverride(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    cache : VesselServiceOverrideCache,
    { id } : VesselServiceOverrideParams
) {
    const model = cache.VesselServiceOverrideResponses.update1(null, { VesselId: id }),
        url = entrypoints.VesselServiceOverride({ Id: id }),
        request = ajax.Url<{}, VesselServiceOverrideResponseData>(url).Get(),
        vessel = S(() => model.Vessel() === null ? null : VesselViewModel(model.Vessel()!));

    request.done(json =>
        cache.VesselServiceOverrideResponses.update1(request.Date, json)
    );

    return {
        Model: model,
        Vessel: vessel,
        Countries: lift(() => Array.from(data.Countries.items())).sort(Country.SortOrder),
        GoBack: () => window.history.back(),
        EditSources:() => window.location.hash = '#!vesselserviceconfigure?id=' + id
    };

    function VesselViewModel(vessel : Vessel) : VesselViewModel {
        const override = S(() => vessel.OverrideServiceVessel() && OverrideServiceVesselViewModel(vessel.OverrideServiceVessel()!)),
            client = ajax.Url<Vessel>(() => vessel.Url() || ''),
            draft = AutoSavingDraftObject(vessel, client, ['Status']),
            // TH: NOTE -- The precedences for flagId and in the return statement reflect the server-side VesselPrecedenceService.
            flagId = applyNumericOverride(v => v.FlagId);

        return {
            Model: draft,
            OverrideServiceVessel: override,
            Name: applyStringOverride(v => v.Name),
            VIN: applyStringOverride(v => v.VIN, [v => v.PSixServiceVessel]),
            IMO: applyStringOverride(v => v.IMO),
            MMSID: applyStringOverride(v => v.MMSID),
            CallSign: applyStringOverride(v => v.CallSign),
            Beam: applyMeasurementOverride(v => v.Beam),
            Length: applyMeasurementOverride(v => v.Length),
            GrossTonnage: applyMeasurementOverride(v => v.GrossTonnage, [v => v.MarineTrafficServiceVessel, v => v.LloydServiceVessel, v => v.PSixServiceVessel]),
            IsGovernmentVessel: applyBooleanOverride(v => v.IsGovernmentVessel),
            VesselType: applyStringOverride(v => v.VesselType, [v => v.LloydServiceVessel, v => v.MarineTrafficServiceVessel]),
            IsCruiseVessel: applyBooleanOverride(v => v.IsCruiseVessel),
            MaxDraft: applyMeasurementOverride(v => v.MaxDraft),
            ExpectedPassengerCount: applyNumericOverride(v => v.ExpectedPassengerCount),
            Flag: data.Countries.index(() => flagId() === null ? -1 : flagId()!)
        };

        function applyStringOverride(
            prop : BaseVesselProperty<DataSignal<string | null>>,
            precedences? : VesselPrecedenceOrder
        ) {
            return applyOverride<DataSignal<string | null>, string | null>(
                res => !!res,
                p => p(),
                prop,
                precedences
            );
        }

        function applyMeasurementOverride(
            prop : BaseVesselProperty<NullableMeasurement>,
            precedences? : VesselPrecedenceOrder
        ) {
            return applyOverride<NullableMeasurement, NullableMeasurement>(
                res => res.Num() !== null,
                p => p,
                prop,
                precedences
            );
        }

        function applyBooleanOverride(
            prop : BaseVesselProperty<DataSignal<boolean | null>>,
            precedences? : VesselPrecedenceOrder
        ) {
            return applyOverride<DataSignal<boolean | null>, boolean | null>(
                res => res !== null,
                p => p(),
                prop,
                precedences
            );
        }

        function applyNumericOverride(
            prop : BaseVesselProperty<DataSignal<number | null>>,
            precedences? : VesselPrecedenceOrder
        ) {
            return applyOverride<DataSignal<number | null>, number | null>(
                res => res !== null,
                p => p(),
                prop,
                precedences
            );
        }

        function applyOverride<TProp, TResult>(
            populated : (res : TResult) => boolean,
            accessor : (prop : TProp) => TResult,
            prop : BaseVesselProperty<TProp>,
            precedences? : VesselPrecedenceOrder
        ) {
            const _precedences = precedences || defaultVesselPrecedenceOrder;
            return () => {
                const o = override() && override()!.Model;
                if (o && populated(accessor(prop(o)))) return accessor(prop(o));
                for (let i = 0; i < _precedences.length; i++) {
                    const p = _precedences[i](vessel)();
                    if (p && populated(accessor(prop(p)))) return accessor(prop(p));
                }
                return accessor(prop(vessel));
            };
        }
    }

    function OverrideServiceVesselViewModel(overrideServiceVessel : OverrideServiceVessel) : OverrideServiceVesselViewModel {
        const client = ajax.Url<OverrideServiceVessel>(() => overrideServiceVessel.Url() || '', { useCache: true }),
            draft = AutoSavingDraftObject(overrideServiceVessel, client, [
                'Beam.Num',
                'CallSign',
                'FlagId',
                'GrossTonnage.Num',
                'IMO',
                'IsGovernmentVessel',
                'Length.Num',
                'MMSID',
                'Name',
                'VIN',
                'MaxDraft.Num',
                'VesselType',
                'IsCruiseVessel',
                'ExpectedPassengerCount'
            ]),
            callSign = coerceEmptyToNull(draft.CallSign),
            name = coerceEmptyToNull(draft.Name),
            imo = coerceEmptyToNull(draft.IMO),
            vesselType = coerceEmptyToNull(draft.VesselType);

        draft.Flag = S.value(S.sample(overrideServiceVessel.Flag));
        data.OverrideServiceVessels.attach(draft, [draft.Flag]);

        return {
            Model: draft,
            CallSign: callSign,
            Name: name,
            IMO: imo,
            VesselType: vesselType
        };

        function coerceEmptyToNull(signal : DataSignal<string | null>) {
            return (val? : string) => {
                if (val === undefined) return signal();
                return signal(val === '' ? null : val);
            };
        }
    }
}
export type VesselServiceOverride = ReturnType<typeof VesselServiceOverride>;


// WEBPACK FOOTER //
// ./apps/portcall/vesselserviceoverride/model.ts